/**
 * Education Section Component
 * Migrated to TypeScript with subcomponents
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaCalendarAlt, FaAward, FaCode, FaDatabase, FaShieldAlt, FaCogs, FaChartLine } from 'react-icons/fa';
import { SiPython, SiCplusplus } from 'react-icons/si';
import { GiBrain, GiProcessor } from 'react-icons/gi';
import { education } from '../../data/education';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const Education: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const getCourseIcon = (courseName: string) => {
    const iconMap: Record<string, any> = {
      'Analysis of Algorithms': FaCode,
      'Machine Learning': GiBrain,
      'Computer Security': FaShieldAlt,
      'Deep Learning': GiBrain,
      'Operating Systems': GiProcessor,
      'Data Structures & Algorithms': FaCode,
      'Computer Architecture': FaCogs,
      'IoT': FaCogs,
      'C/C++': SiCplusplus,
      'Python': SiPython,
      'Discrete Mathematics': FaChartLine,
      'Linear Algebra': FaChartLine,
      'Probability and Statistics': FaChartLine,
      'SQL & NoSQL Database': FaDatabase
    };
    return iconMap[courseName] || FaCode;
  };

  const getCourseColor = (courseName: string) => {
    const colorMap: Record<string, string> = {
      'Analysis of Algorithms': '#4A90E2',
      'Machine Learning': '#FF6B6B',
      'Computer Security': '#DC3545',
      'Deep Learning': '#4ECDC4',
      'Operating Systems': '#5C5C5C',
      'Data Structures & Algorithms': '#4A90E2',
      'Computer Architecture': '#F39C12',
      'IoT': '#16A34A',
      'C/C++': '#00599C',
      'Python': '#3776AB',
      'Discrete Mathematics': '#8E44AD',
      'Linear Algebra': '#E74C3C',
      'Probability and Statistics': '#F39C12',
      'SQL & NoSQL Database': '#336791'
    };
    return colorMap[courseName] || '#6C757D';
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="education" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                Education
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Academic background and achievements
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b accent-gradient-bg rounded-full hidden lg:block"></div>

            <div className="space-y-12">
              {education.map((edu, index) => (
                <div
                  key={edu.id}
                  className={`flex items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Timeline Dot */}
                  <div className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 ${colors.bg} rounded-full border-4 border-white dark:border-gray-800 z-10`}></div>

                  {/* Content Card */}
                  <div className={`w-full lg:w-5/12 ${
                    index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8'
                  }`}>
                    <div className={`${colors.cardBackground} rounded-2xl p-8 shadow-lg border border-gray-300 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:scale-105`}>
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            {edu.degree}
                          </h3>
                          <div className={`flex items-center gap-3 text-lg font-semibold ${colors.text} mb-2`}>
                            {edu.schoolLogo && (
                              <div className="w-8 h-8 flex-shrink-0">
                                {React.createElement(edu.schoolLogo, { className: "w-full h-full" })}
                              </div>
                            )}
                            <span>{edu.school}</span>
                          </div>
                          <div className={`flex items-center ${colors.textSecondary} mb-1`}>
                            <FaMapMarkerAlt className="w-4 h-4 mr-2" />
                            {edu.location}
                          </div>
                          <div className={`flex items-center ${colors.textSecondary} mb-4`}>
                            <FaCalendarAlt className="w-4 h-4 mr-2" />
                            {edu.date}
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 text-sm font-medium rounded-full">
                          {edu.gpa ? `GPA: ${edu.gpa}` : 'Degree'}
                        </span>
                      </div>

                      {/* Details */}
                      {edu.details && edu.details.length > 0 && (
                        <div className="mb-6">
                          <h4 className={`text-lg font-semibold ${colors.textPrimary} mb-3`}>
                            Key Highlights
                          </h4>
                          <ul className="space-y-2">
                            {edu.details.map((detail, i) => (
                              <li key={i} className={`flex items-start ${colors.textSecondary}`}>
                                <span className={`${colors.text} mr-3 mt-1`}>•</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Courses */}
                      {edu.courses && edu.courses.length > 0 && (
                        <div>
                          <h4 className={`text-lg font-semibold ${colors.textPrimary} mb-3 flex items-center`}>
                            <FaAward className="w-5 h-5 mr-2" />
                            Relevant Courses
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {edu.courses.map((course, courseIndex) => {
                              const Icon = getCourseIcon(course);
                              return (
                                <div
                                  key={courseIndex}
                                  className={`flex items-center space-x-2 px-3 py-1 rounded-full ${colors.background} border border-gray-300 dark:border-gray-600 shadow-lg hover:shadow-xl transition-all duration-300`}
                                >
                                  <Icon 
                                    className="w-4 h-4 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                                    style={{ color: getCourseColor(course) }}
                                  />
                                  <span className={`text-sm ${colors.textPrimary}`}>{course}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;

